import {
  Button,
  Dropdown,
  Form,
  Image,
  Input,
  Menu,
  Typography,
} from "antd";

import {
  ArrowRightOutlined,
  DownOutlined,
  GlobalOutlined,
  LockOutlined,
  MailOutlined,
  MobileOutlined,
  UserOutlined,
} from "@ant-design/icons";

import React, { useState } from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { translation } from "../../../../i18n/translationConstant";
import { UserLoginSuccessResponse } from "../../../../Models/Response/Authentication/Otp";

import {
  sendOtp,
  verifyOtp,
} from "../../../../Services/AuthenticationServices/AuthenticationServices";

import { LoginSignupFormType } from "../../../../Utils/Constants/loginSignupFormTypes";
import { Redirection } from "../../../../Utils/Constants/redirection";

import PinInputComponent from "../../../Common/Components/PinInput";
import UserIdentification from "../../../Identifiers/UserIdentification";
import ForgotPassword from "../ForgotPassword";
import UserInformation from "../UserInformation";

import "./index.css";

interface ILoginSignupFormProps
  extends React.PropsWithChildren,
    WithTranslation {
  formType: string;
  formSubmit: (values: any) => void;
  buttonSubSectionText: string;
  buttonSubSectionButton: string;
  className?: string;
  subButtonClick: () => void;
  buttonText: string;
  isLoginWithPin?: (is: boolean) => void;
  phoneNumber?: string;
  email?: string;
  isVuaIdDisabled?: boolean;
}

enum FormType {
  LOGIN = "LOGIN",
  CreateAccount = "Create Account",
}

const LoginSignupForm: React.FC<ILoginSignupFormProps> = ({
  t,
  i18n,
  className,
  isLoginWithPin,
  children,
  formType,
  formSubmit,
  buttonSubSectionButton,
  buttonSubSectionText,
  buttonText,
  subButtonClick,
  phoneNumber,
  email,
  isVuaIdDisabled,
}) => {
  const [isLoginWithPinState, setIsLoginWithPin] = useState(true);

  const [form] = Form.useForm();

  const [token, setToken] = useState("");
  const [otpUniqueID, setOTPUniqueId] = useState("");
  const [mobile, setmobile] = useState("");

  const loading: boolean = useSelector(
    (state: any) => state.loader.searchLoader
  );

  const [visible, setVisibility] = useState(false);

  const [isAddIdentifierVerification, setVerification] =
    useState(false);

  const isLogin = formType === FormType.LOGIN;
  const isSignup = formType === FormType.CreateAccount;

  /*
   * =========================================================
   * LANGUAGE
   * =========================================================
   */

  const getCurrentLanguage = () => {
    const storedLanguage = localStorage.getItem("i18nextLng");

    if (storedLanguage) {
      return storedLanguage.split("-")[0];
    }

    if (i18n && i18n.language) {
      return i18n.language.split("-")[0];
    }

    return "en";
  };

  const [selectedLanguage, setSelectedLanguage] = useState(
    getCurrentLanguage()
  );

  const languageLabels: {
    [key: string]: {
      name: string;
      native: string;
    };
  } = {
    en: {
      name: "English",
      native: "Hello",
    },
    hi: {
      name: "हिंदी",
      native: "नमस्ते",
    },
    mr: {
      name: "मराठी",
      native: "नमस्कार",
    },
  };

  const changeLanguage = (language: string) => {
    i18n.changeLanguage(language);

    localStorage.setItem("i18nextLng", language);

    setSelectedLanguage(language);
  };

  const languageMenu = (
    <Menu
      selectedKeys={[selectedLanguage]}
      className="languageDropdownMenu"
    >
      {Object.keys(languageLabels).map((language) => (
        <Menu.Item
          key={language}
          onClick={() => changeLanguage(language)}
        >
          <div className="languageMenuItem">
            <div className="languageMenuText">
              <span className="languageMenuName">
                {languageLabels[language].name}
              </span>

              <span className="languageMenuNative">
                {languageLabels[language].native}
              </span>
            </div>

            {selectedLanguage === language && (
              <span className="languageSelectedMark">
                ✓
              </span>
            )}
          </div>
        </Menu.Item>
      ))}
    </Menu>
  );

  /*
   * =========================================================
   * MOBILE NUMBER
   * =========================================================
   */

  const onMobileChange = (e: any) => {
    let value = e.target.value || "";

    value = value.replace(/[^0-9]/g, "");

    if (value.charAt(0) === "0") {
      value = value.substring(1);
    }

    e.target.value = value;
  };

  let disableInputPhoneNumber = false;

  if (!!phoneNumber && phoneNumber.length === 10) {
    form.setFieldsValue({ phoneNumber });

    disableInputPhoneNumber = true;
  }

  /*
   * =========================================================
   * LOGIN WITH PIN
   * =========================================================
   */

  const loginWithPin = () => {
    setIsLoginWithPin(!isLoginWithPinState);

    if (isLoginWithPin) {
      isLoginWithPin(!isLoginWithPinState);
    }

    if (isLoginWithPinState) {
      form.setFieldsValue({ pin: "" });
    }
  };

  /*
   * =========================================================
   * VUA ID VALIDATION
   * =========================================================
   */

  const checkVuaId = (_: any, value: string) => {
    if (
      value &&
      value.length === 10 &&
      Number(value) &&
      value !==
        form.getFieldValue(
          LoginSignupFormType.phoneNumber
        )
    ) {
      return Promise.reject(
        new Error(
          t(translation.errofCustomValidation)
        )
      );
    }

    return Promise.resolve();
  };

  /*
   * =========================================================
   * SET VUA ID AS MOBILE NUMBER
   * =========================================================
   */

  const setVuaIdAsMobileNumber = (change: any) => {
    const phoneNumberField = change.filter(
      (c: any) =>
        c.name.toString() ===
        LoginSignupFormType.phoneNumber
    )[0];

    const vuaIdField = change.filter(
      (c: any) =>
        c.name.toString() ===
        LoginSignupFormType.vuaId
    )[0];

    if (
      phoneNumberField &&
      phoneNumberField.touched &&
      phoneNumberField.value &&
      phoneNumberField.value.length === 10 &&
      !vuaIdField
    ) {
      form.setFieldsValue({
        vuaId: phoneNumberField.value,
      });
    }
  };

  /*
   * =========================================================
   * FORGOT PASSWORD
   * =========================================================
   */

  const onCancel = () => {
    setVisibility(false);
    setVerification(false);
  };

  const onIdentifierEntered = (iNumber: any) => {
    setmobile(iNumber.value);

    sendOtp({
      phoneNumber: iNumber.value,
    })
      .then((res) => res.data)
      .then((res) => {
        setOTPUniqueId(res);
        setVerification(true);
      });
  };

  const onOTPSubmit = (
    otp: string,
    identifierNumber: any
  ) => {
    verifyOtp({
      phone_number: mobile,
      grant_type: "password",
      code: otp,
      otpUniqueID,
    })
      .then((res) => res.data)
      .then((res: UserLoginSuccessResponse) => {

        console.log("OTP verification response:", res);
        console.log("ACCESS TOKEN:", res.access_token);

        form.resetFields();

        // Close forgot-password identifier modal
        setVerification(false);
        setVisibility(false);

        // Open reset PIN popup
        setToken(res.access_token);
      });
  };

  /*
   * =========================================================
   * RENDER
   * =========================================================
   */

  return (
    <>
      {token && <ForgotPassword token={token} />}

      <UserIdentification
        handleCancel={onCancel}
        identifierEntered={onIdentifierEntered}
        isAddIdentifierVerification={
          isAddIdentifierVerification
        }
        onOTPSubmit={onOTPSubmit}
        visible={visible}
        label={"Enter Mobile Number"}
        multipleIdentifiers={false}
        isForgotPassword={true}
        mobileNumber={mobile}
        entityIdentifiers={[]}
      />

      <div className="detailsForm">

        {/* =====================================================
            LANGUAGE SELECTOR
            ===================================================== */}

        <Dropdown
          overlay={languageMenu}
          trigger={["click"]}
          placement="bottomRight"
        >
          <button
            type="button"
            className="languageSelector"
            aria-label="Select language"
          >
            <GlobalOutlined className="languageGlobe" />

            <span className="languageCurrent">
              {languageLabels[selectedLanguage]?.name ||
                "English"}
            </span>

            <DownOutlined className="languageArrow" />
          </button>
        </Dropdown>

        {/* =====================================================
            FORM
            ===================================================== */}

        <Form
          form={form}
          className={`form ${
            isSignup ? "signupForm" : "loginForm"
          }`}
          name="basic"
          onFinish={formSubmit}
          autoComplete="off"
          onFieldsChange={setVuaIdAsMobileNumber}
          scrollToFirstError={true}
        >

          {/* Keeps the form fields and action area together.
              Login is vertically centered; signup flows directly
              from the terms checkbox to the Next button. */}
          <div className={`formLayout ${isLogin ? "loginLayout" : "signupLayout"}`}>

          {/* ===================================================
              FORM BODY
              =================================================== */}

          <div
            className={`${className || ""} formBodyInput`}
          >

            {/* FIU logo when redirected */}

            {/* {localStorage.getItem("redirect") && (
              <Image
                preview={false}
                width={52}
                height={52}
                className="redirectFiuLogo"
                src={
                  localStorage.getItem("fiuId") &&
                  typeof process !== "undefined" &&
                  process.env.REACT_APP_LOGO_BASE_URL
                    ? `${process.env.REACT_APP_LOGO_BASE_URL}${localStorage.getItem(
                        "fiuId"
                      )}.png`
                    : Redirection.FallBackUrl
                }
                fallback={Redirection.FallBackUrl}
              />
            )} */}

            {/* =================================================
                HEADING
                ================================================= */}

            <div className="formHeadingBlock">

              <Typography.Text className="formTypeText">
                {isLogin
                  ? t(translation.lblLogin)
                  : t(translation.lblLetsGetStarted)}
              </Typography.Text>

              <Typography.Text className="formTypeSubText">
                {isLogin
                  ? t(translation.lblLogintxt)
                  : t(translation.lblsignUptxt)}
              </Typography.Text>

            </div>

            {/* =================================================
                SECURITY BADGE
                ================================================= */}

            {/* 
            <div className="securityTag">
              <LockOutlined />
              <span>
                {t(translation.lblRbiTag)}
              </span>
            </div>
            */}

            {/* =================================================
                MOBILE NUMBER
                ================================================= */}

            <Form.Item
              className="inputCss"
              name={LoginSignupFormType.phoneNumber}
              rules={[
                {
                  required: true,
                  message: t(
                    translation.errorRequiredPhoneNumber
                  ),
                },
                {
                  len: 10,
                  message: t(
                    translation.errorInvalidPhoneNumber
                  ),
                },
              ]}
            >
              <Input
                prefix={
                  <MobileOutlined className="fieldIcon" />
                }
                id="mobile"
                type="tel"
                onInput={onMobileChange}
                autoComplete="off"
                maxLength={10}
                placeholder={t(
                  translation.lblMobileNumber
                )}
                disabled={disableInputPhoneNumber}
              />
            </Form.Item>

            {/* =================================================
                LOGIN PIN
                ================================================= */}

            {isLoginWithPinState && isLogin && (
              <div className="loginPinSection">

                <PinInputComponent
                  className="inputCss1"
                />

                <Typography.Text
                  className="forgotPin"
                  onClick={() =>
                    setVisibility(true)
                  }
                >
                  {t(
                    translation.lblForgotPassword
                  )}
                </Typography.Text>

              </div>
            )}

            {/* =================================================
                SIGNUP FIELDS
                ================================================= */}

            {isSignup && (
              <div className="signupFields">

                <UserInformation
                  vuaIdValidator={checkVuaId}
                  vuaId={
                    !!phoneNumber
                      ? `${phoneNumber}`
                      : ""
                  }
                  isVuaIdDisabled={
                    isVuaIdDisabled
                  }
                  email={email || ""}
                  form={form}
                />

              </div>
            )}

            {children}

          </div>

          {/* ===================================================
              FOOTER
              =================================================== */}

          <div className="formFooter">

            <Form.Item className="otpButton">

              <Button
                loading={loading}
                className="formItemButton"
                htmlType="submit"
              >

                <span>
                  {isLogin
                    ? t(translation.lblLogin)
                    : buttonText}
                </span>

                <ArrowRightOutlined />

              </Button>

            </Form.Item>

            {!disableInputPhoneNumber && (
              <div className="dontHaveAccountFooter">

                <Typography.Text className="donthaveaccountText">
                  {buttonSubSectionText}
                </Typography.Text>

                <Button
                  className="switchAccountButton"
                  onClick={subButtonClick}
                  id="createAccountButton"
                  type="text"
                >
                  {buttonSubSectionButton}
                </Button>

              </div>
            )}

          </div>

          </div>
        </Form>
      </div>
    </>
  );
};

export default withTranslation()(LoginSignupForm);